import { useLocale } from '@solinkcorp/locale';
import { type Immutable } from 'immer';
import { useMemo } from 'react';
import { type ServiceDisplayWidget } from '../layout/types';

interface UseValidationMessageParams {
  widget: Immutable<ServiceDisplayWidget>;
  hasConnectedTags: boolean;
  editing: boolean | undefined;
  hasLine: boolean;
  hasMultipleAssignments: boolean;
  hasZoneAndLine: boolean;
  aiTagName: string | undefined;
}

export interface ValidationMessage {
  text: string;
  hasTooltip: boolean;
  tooltipText?: string;
}

export function useValidationMessage({
  widget,
  hasConnectedTags,
  editing,
  hasLine,
  hasMultipleAssignments,
  hasZoneAndLine,
  aiTagName,
}: UseValidationMessageParams): ValidationMessage | null {
  const locale = useLocale();

  return useMemo(() => {
    // Live feed widgets explain tag and connectivity problems on the footage itself, so a second
    // message under the title would just repeat them.
    if (widget.config.type === 'LIVE_FEED') {
      return null;
    }
    if (widget.tagIds.length === 0) {
      return { text: locale.serviceDisplayMonitor.noAiTagsConfigured, hasTooltip: false };
    }
    if (!hasConnectedTags && !editing) {
      return {
        text: locale.serviceDisplayMonitor.linkWidgetToTracking,
        hasTooltip: true,
        tooltipText: locale.serviceDisplayMonitor.learnMore,
      };
    }
    if (hasLine && (widget.config.type === 'TIME' || widget.config.type === 'TOTAL_JOURNEY_TIME')) {
      return { text: locale.serviceDisplayMonitor.line, hasTooltip: false };
    }
    if (hasMultipleAssignments && aiTagName) {
      return {
        text: `${aiTagName} ${locale.serviceDisplayMonitor.multipleAssignments}`,
        hasTooltip: false,
      };
    }
    if (hasZoneAndLine && aiTagName) {
      return {
        text: `${aiTagName} ${locale.serviceDisplayMonitor.zoneAndLineConfigured}`,
        hasTooltip: false,
      };
    }
    return null;
  }, [
    widget.tagIds.length,
    hasConnectedTags,
    editing,
    hasLine,
    widget.config.type,
    hasMultipleAssignments,
    locale,
    aiTagName,
    hasZoneAndLine,
  ]);
}
